import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, CheckCircle2, Clock, Download, FileText, Loader2, ScanSearch } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Último barrido de listas de la entidad (manual o automático nocturno), con su constancia.
 * El cotejo es 100 % local: listas propias + Centro de Listas vigente, sin consultas a
 * proveedores externos. El historial incluye, aparte, los barridos con cotejo externo.
 */
export default function BarridoListasCard({ refreshKey = 0, job = null }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [historial, setHistorial] = useState(false);
  const [detalles, setDetalles] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get("/pld/barrido-listas/estado?limit=60");
      setData(r && typeof r === "object" && !Array.isArray(r) ? r : null);
    } catch (e) {
      setData(null);
      if (![401, 403].includes(e?.response?.status)) toast.error(apiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const descargar = async (id, folio) => {
    try {
      const res = await api.get(`/pld/barrido-listas/${id}/constancia`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `constancia_barrido_${folio || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const descargarExterno = async (id, fecha) => {
    try {
      const res = await api.get(`/pld/barrido/${id}/constancia`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `constancia_cotejo_externo_${fecha}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const verDetalles = async (id) => {
    try {
      const { data: r } = await api.get(`/pld/barrido-listas/${id}/detalles`);
      setDetalles(r);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (cargando) {
    return (
      <div className="animate-pulse rounded-lg border border-slate-200 bg-white px-4 py-3 text-xs text-slate-400"
           data-testid="barrido-listas-cargando">
        Cargando barrido de listas…
      </div>
    );
  }
  if (!data) return null;

  const ultimo = data.ultimo || null;
  const sinCoincidencia = ultimo?.resultado === "sin_coincidencia";
  const historialLocal = data.historial || [];
  const externos = data.externos || [];

  return (
    <div data-testid="barrido-listas-card">
      {job?.estado === "en_curso" && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5 text-xs text-sky-900"
             data-testid="barrido-listas-en-curso">
          <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
          <span>
            <b>Barrido en curso</b>
            {job.total_sujetos
              ? ` · ${(job.cotejadas || 0).toLocaleString("es-MX")} de ${job.total_sujetos.toLocaleString("es-MX")} sujeto(s) cotejado(s)`
              : " · leyendo listas y sujetos"}
            {" — corre en segundo plano; puedes seguir trabajando."}
          </span>
        </div>
      )}
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
        !ultimo ? "border-slate-200 bg-slate-50"
          : sinCoincidencia ? "border-emerald-200 bg-emerald-50"
            : "border-amber-300 bg-amber-50"
      }`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            {!ultimo
              ? <Clock className="h-5 w-5 text-slate-400" data-testid="barrido-listas-icono-pendiente" />
              : sinCoincidencia
                ? <CheckCircle2 className="h-5 w-5 text-emerald-600" data-testid="barrido-listas-icono-verde" />
                : <AlertTriangle className="h-5 w-5 text-amber-600" data-testid="barrido-listas-icono-ambar" />}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Barrido de listas</div>
            {!ultimo ? (
              <div className="text-xs text-slate-600" data-testid="barrido-listas-vacio">
                Todavía no hay barridos registrados. Usa «Correr barrido» arriba. Además, cada
                vez que se publica una lista nueva, sus registros nuevos se cotejan contra
                toda la cartera en la ventana nocturna.
              </div>
            ) : (
              <>
                <div className="text-xs text-slate-700" data-testid="barrido-listas-resumen">
                  Último barrido: <b data-testid="barrido-listas-fecha">{ultimo.ejecutado_en || ultimo.fecha}</b>
                  {" — "}
                  <b data-testid="barrido-listas-resultado">
                    {sinCoincidencia
                      ? "sin coincidencias"
                      : `con ${ultimo.total_coincidencias} coincidencia(s)`}
                  </b>
                  {" · "}{ultimo.total_sujetos} sujeto(s) cotejado(s)
                  {" · "}{ultimo.tipo === "automatico" ? "automático nocturno" : "manual"}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500" data-testid="barrido-listas-folio">
                  Folio {ultimo.folio} · sello SHA-256 {String(ultimo.sello_sha256 || "").slice(0, 16)}…
                  {" · "}cotejo contra listas propias y Centro de Listas vigente
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {ultimo && ultimo.resultado === "con_coincidencia" && (
            <Button size="sm" variant="outline" onClick={() => verDetalles(ultimo.id)}
                    data-testid="barrido-listas-btn-detalles">
              Ver coincidencias
            </Button>
          )}
          {ultimo && (
            <Button size="sm" onClick={() => descargar(ultimo.id, ultimo.folio)}
                    data-testid="barrido-listas-btn-constancia"
                    className="bg-primary hover:bg-sidebar-hover">
              <Download className="mr-1 h-3.5 w-3.5" /> Constancia PDF
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setHistorial(true)}
                  data-testid="barrido-listas-btn-historial">
            <FileText className="mr-1 h-3.5 w-3.5" /> Historial
          </Button>
        </div>
      </div>

      {/* Historial */}
      <Dialog open={historial} onOpenChange={setHistorial}>
        <DialogContent className="max-w-4xl" data-testid="dialog-barrido-listas-historial">
          <DialogHeader>
            <DialogTitle>Historial de barridos</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-5 overflow-y-auto">
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <ScanSearch className="h-3.5 w-3.5" /> Barridos de listas (cotejo interno)
              </p>
              {!historialLocal.length ? (
                <div className="py-4 text-center text-sm text-slate-500">Sin barridos registrados.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Folio</TableHead>
                      <TableHead>Fecha y hora</TableHead>
                      <TableHead>Modalidad</TableHead>
                      <TableHead>Resultado</TableHead>
                      <TableHead className="text-right">Sujetos</TableHead>
                      <TableHead className="text-right">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historialLocal.map((b) => (
                      <TableRow key={b.id} data-testid={`fila-barrido-${b.id}`}>
                        <TableCell className="font-mono text-xs">{b.folio}</TableCell>
                        <TableCell className="text-sm">{b.ejecutado_en || b.fecha}</TableCell>
                        <TableCell className="text-xs">
                          {b.tipo === "automatico" ? "Automático" : "Manual"}
                          <span className="block text-[11px] text-slate-400">{b.usuario}</span>
                        </TableCell>
                        <TableCell>
                          {b.resultado === "sin_coincidencia"
                            ? <Badge className="bg-emerald-100 text-emerald-700">Sin coincidencias</Badge>
                            : <Badge className="bg-amber-100 text-amber-700">Con {b.total_coincidencias}</Badge>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{b.total_sujetos}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => descargar(b.id, b.folio)}
                                  data-testid={`hist-barrido-pdf-${b.id}`}>
                            <Download className="mr-1 h-3 w-3" /> PDF
                          </Button>
                          {b.resultado === "con_coincidencia" && (
                            <Button size="sm" variant="ghost" className="ml-1"
                                    onClick={() => verDetalles(b.id)}
                                    data-testid={`hist-barrido-det-${b.id}`}>
                              Detalles
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {externos.length > 0 && (
              <div data-testid="barrido-listas-externos">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Barridos con cotejo externo (operados por la plataforma)
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha de corte</TableHead>
                      <TableHead>Resultado</TableHead>
                      <TableHead className="text-right">Clientes</TableHead>
                      <TableHead className="text-right">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {externos.map((c) => (
                      <TableRow key={c.id} data-testid={`fila-externo-${c.id}`}>
                        <TableCell className="text-sm">{c.fecha_corte}</TableCell>
                        <TableCell>
                          {c.estado === "sin_coincidencia"
                            ? <Badge className="bg-emerald-100 text-emerald-700">Sin coincidencias</Badge>
                            : <Badge className="bg-amber-100 text-amber-700">Con {c.total_coincidencias}</Badge>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{c.total_clientes_barridos}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline"
                                  onClick={() => descargarExterno(c.id, c.fecha_corte)}
                                  data-testid={`hist-externo-pdf-${c.id}`}>
                            <Download className="mr-1 h-3 w-3" /> PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistorial(false)}
                    data-testid="dialog-barrido-listas-cerrar">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalles de coincidencias */}
      <Dialog open={!!detalles} onOpenChange={(v) => !v && setDetalles(null)}>
        <DialogContent className="max-w-4xl" data-testid="dialog-barrido-listas-detalles">
          <DialogHeader>
            <DialogTitle>Coincidencias del barrido {detalles?.folio}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {detalles && (
              <div className="mb-3 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs sm:grid-cols-4"
                   data-testid="barrido-listas-trazabilidad">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Fecha y hora</p>
                  <p className="mt-0.5 font-medium text-slate-800">{detalles.ejecutado_en}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Ejecutado por</p>
                  <p className="mt-0.5 font-medium text-slate-800">{detalles.usuario}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Sujetos cotejados</p>
                  <p className="mt-0.5 font-medium text-slate-800">{detalles.total_sujetos}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">Listas cotejadas</p>
                  <p className="mt-0.5 font-medium text-slate-800">{(detalles.listas || []).length}</p>
                </div>
              </div>
            )}
            {!detalles?.coincidencias?.length ? (
              <div className="py-6 text-center text-sm text-slate-500">Sin coincidencias.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sujeto</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Lista</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead>Coincidencia</TableHead>
                    <TableHead>Crítica</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detalles.coincidencias.map((c, i) => (
                    <TableRow key={c.match_id || i} data-testid={`fila-coincidencia-${i}`}>
                      <TableCell className="text-sm">
                        {c.persona_nombre}
                        <span className="block font-mono text-[11px] text-slate-400">{c.persona_rfc || "—"}</span>
                      </TableCell>
                      <TableCell className="text-xs">{c.persona_tipo}</TableCell>
                      <TableCell className="text-xs">
                        {c.lista_nombre}
                        <span className="block text-[11px] text-slate-400">v{c.lista_version}</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{c.score}</TableCell>
                      <TableCell className="text-xs">{c.tipo_coincidencia} ({c.campo_coincidente})</TableCell>
                      <TableCell className="text-xs">{c.critica ? "Sí" : "No"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <p className="mt-3 text-[11px] text-slate-500">
              El barrido no bloquea a nadie: cada coincidencia queda en la bandeja de
              Coincidencias para que el Oficial de Cumplimiento la confirme o la descarte como
              falso positivo. Ese acto es el que activa el bloqueo operativo.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalles(null)}
                    data-testid="dialog-barrido-listas-det-cerrar">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
